import { IconX } from '@tabler/icons-solidjs'
import {
  createEffect,
  createUniqueId,
  type JSX,
  onCleanup,
  onMount,
  Show,
  splitProps,
} from 'solid-js'
import { cn } from '../lib/cn.ts'
import { tv, type VariantProps } from '../lib/tv.ts'

/**
 * Native <dialog>, so focus trapping, Escape and inertness come from the platform rather than
 * from a scroll-lock hack that breaks on the shop floor's iPad.
 *
 * Structure is header / body / footer, and `footer` is a prop rather than something the caller
 * drops into the body. A dialog whose confirm button floats in the prose has no visual anchor for
 * the decision, which is exactly what made the first version read as unfinished.
 *
 * Motion: the panel scales from 0.985, never from 0 — nothing in the physical world appears out
 * of literal nothing. Exit is immediate; `close()` on a native dialog is synchronous and a
 * modal that lingers on the way out is a modal you are waiting for.
 */
const dialog = tv({
  slots: {
    root: [
      'm-auto w-[calc(100%-2rem)] rounded-panel bg-surface p-0 text-text shadow-overlay',
      'backdrop:bg-ink/45 backdrop:backdrop-blur-[2px]',
      'open:animate-[surface-in_200ms_var(--ease-overlay)]',
      'backdrop:animate-[overlay-in_200ms_var(--ease-out)]',
    ],
    header: 'flex items-start justify-between gap-4 px-control-x-lg pt-control-x-lg pb-3',
    heading: 'grid gap-1',
    title: 'text-lead font-semibold tracking-tight text-text',
    description: 'text-body text-text-subtle',
    close: [
      'flex size-7 shrink-0 items-center justify-center rounded-control max-md:size-(--size-touch)',
      'text-text-subtle transition-colors duration-(--duration-press) ease-out',
      'hover:bg-surface-sunken hover:text-text',
      'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent',
    ],
    body: 'px-control-x-lg pb-control-x-lg text-body',
    footer:
      'flex items-center justify-end gap-2 border-t border-border bg-surface-muted px-control-x-lg py-3',
  },
  variants: {
    size: {
      sm: { root: 'max-w-sm' },
      md: { root: 'max-w-md' },
      lg: { root: 'max-w-2xl' },
    },
  },
  defaultVariants: { size: 'md' },
})

export interface DialogProps
  extends Omit<JSX.DialogHtmlAttributes<HTMLDialogElement>, 'children' | 'open' | 'title'>,
    VariantProps<typeof dialog> {
  open: boolean
  title: string
  /** One line under the title. Use it for the consequence, not for a restatement of the title. */
  description?: string
  closeLabel?: string
  /** Whether a click on the native backdrop closes the dialog. Defaults to true. */
  dismissable?: boolean
  onClose: () => void
  /** Action row. Destructive or primary action rightmost, matching platform convention. */
  footer?: JSX.Element
  children: JSX.Element
}

export const Dialog = (props: DialogProps) => {
  const [local, rest] = splitProps(props, [
    'open',
    'title',
    'description',
    'closeLabel',
    'dismissable',
    'onClose',
    'footer',
    'size',
    'children',
    'class',
  ])
  const styles = () => dialog({ size: local.size })
  const titleId = createUniqueId()
  const descriptionId = createUniqueId()
  let element!: HTMLDialogElement

  createEffect(() => {
    if (local.open && !element.open) element.showModal()
    if (!local.open && element.open) element.close()
  })

  onMount(() => {
    const dismiss = (event: MouseEvent): void => {
      if (event.target === element && local.dismissable !== false) local.onClose()
    }
    element.addEventListener('click', dismiss)
    onCleanup(() => element.removeEventListener('click', dismiss))
  })

  return (
    <dialog
      {...rest}
      ref={element}
      aria-labelledby={titleId}
      aria-describedby={local.description ? descriptionId : undefined}
      class={cn(styles().root(), local.class)}
      onCancel={(event) => {
        event.preventDefault()
        local.onClose()
      }}
    >
      <header class={styles().header()}>
        <div class={styles().heading()}>
          <h2 id={titleId} class={styles().title()}>
            {local.title}
          </h2>
          <Show when={local.description}>
            {(description) => (
              <p id={descriptionId} class={styles().description()}>
                {description()}
              </p>
            )}
          </Show>
        </div>
        <button
          type="button"
          class={styles().close()}
          aria-label={local.closeLabel ?? 'Закрити'}
          onClick={() => local.onClose()}
        >
          <IconX size={16} strokeWidth={1.5} aria-hidden="true" />
        </button>
      </header>

      <div class={styles().body()}>{local.children}</div>

      <Show when={local.footer}>
        {(footer) => <footer class={styles().footer()}>{footer()}</footer>}
      </Show>
    </dialog>
  )
}
